import { FC, useMemo } from "react"
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { Add, DeleteOutline } from "@mui/icons-material"
import { Userbot, UserbotCatalog } from "../../interfaces"
import { useCatalog } from "../../hooks"
import { defaultCondition, validateConditionExpr } from "./conditionExpr"
import { CONDITION_HINTS, TEMPLATE_HINTS } from "../../constants/hints"

interface IProps {
  value: Userbot.ConditionExpr | null
  onChange: (v: Userbot.ConditionExpr | null) => void
  /** Подсказки голов для автокомплита left/right/value */
  headHints?: string[]
  /** Лимиты из catalog.limits; если не передан — берётся из useCatalog() */
  limits?: UserbotCatalog.CatalogLimits
  error?: string
}

/** Создаёт ConditionExpr нужной формы для заданного op. */
const createForOp = (op: string): Userbot.ConditionExpr => {
  switch (op) {
    case "eq":
    case "ne":
    case "gt":
    case "gte":
    case "lt":
    case "lte":
      return { op, left: "", right: "" }
    case "in":
    case "nin":
      return { op, left: "", right: [] }
    case "exists":
    case "empty":
      return { op, value: "" }
    case "regex":
      return { op, value: "", pattern: "", flags: "" }
    case "and":
    case "or":
      return { op, conditions: [defaultCondition()] }
    case "not":
      return { op, condition: defaultCondition() }
    default:
      return { op: "eq", left: "", right: "" }
  }
}

/** Преобразует unknown → строку для TextField. */
const toStr = (v: unknown): string =>
  v === undefined || v === null ? "" : String(v)

const COMPARISON_OPS = new Set(["eq", "ne", "gt", "gte", "lt", "lte"])

export const ConditionExprEditor: FC<IProps> = ({
  value,
  onChange,
  headHints = [],
  limits: limitsProp,
  error,
}) => {
  const catalog = useCatalog()
  const limits = limitsProp ?? catalog.limits
  const conditionOps = catalog.conditionOps
  const allHints = useMemo(() => [...headHints, ...TEMPLATE_HINTS], [headHints])

  if (!value) {
    return (
      <Box>
        <Button
          size="small"
          startIcon={<Add />}
          onClick={() => onChange(defaultCondition())}
        >
          Настроить условие
        </Button>
        {error && (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        )}
      </Box>
    )
  }

  const op = value.op
  const descriptor = conditionOps.find((d) => d.op === op)
  const operands = descriptor?.operands ?? []
  const errs = validateConditionExpr(value, limits)
  const depthLimitReached =
    limits && (op === "and" || op === "or" || op === "not")
      ? errs.some((e) => e.includes("depth"))
      : false

  const patch = (over: Partial<Userbot.ConditionExpr>) =>
    onChange({ ...value, ...over } as Userbot.ConditionExpr)

  const renderOperandField = (
    operand: Userbot.ConditionOperand,
    label: string,
  ) => (
    <Autocomplete
      freeSolo
      options={allHints}
      value={toStr((value as Record<string, unknown>)[operand])}
      onInputChange={(_, v) => patch({ [operand]: v } as never)}
      renderInput={(p) => (
        <TextField
          {...p}
          label={label}
          size="small"
          helperText={errs.find((e) => e.includes(operand))}
          error={errs.some((e) => e.includes(operand))}
        />
      )}
    />
  )

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        alignItems="flex-start"
        flexWrap="wrap"
      >
        <TextField
          select
          label="Оператор"
          value={op}
          size="small"
          sx={{ minWidth: 130 }}
          onChange={(e) => {
            const newOp = e.target.value
            onChange(createForOp(newOp))
          }}
        >
          {conditionOps.map((d) => (
            <MenuItem key={d.op} value={d.op}>
              {d.op}
            </MenuItem>
          ))}
        </TextField>

        {COMPARISON_OPS.has(op) && (
          <Tooltip
            title={CONDITION_HINTS[op] ?? ""}
            arrow
            sx={{ alignSelf: "center" }}
          >
            <Typography variant="caption" color="text.secondary">
              ⓘ
            </Typography>
          </Tooltip>
        )}
      </Stack>

      <Stack spacing={1} mt={1}>
        {operands.includes("left") && renderOperandField("left", "Left")}
        {operands.includes("right") && (
          <Box>
            {op === "in" || op === "nin" ? (
              <TextField
                label="Right (массив или {{...}})"
                size="small"
                fullWidth
                value={
                  Array.isArray((value as { right: unknown }).right)
                    ? JSON.stringify((value as { right: unknown[] }).right)
                    : toStr((value as { right: unknown }).right)
                }
                onChange={(e) => {
                  const raw = e.target.value
                  if (/^\{\{[\s\S]+\}\}$/.test(raw)) {
                    patch({ right: raw } as never)
                  } else {
                    try {
                      patch({ right: JSON.parse(raw) } as never)
                    } catch {
                      patch({ right: raw } as never)
                    }
                  }
                }}
                helperText={errs.find((e) => e.includes("right"))}
                error={errs.some((e) => e.includes("right"))}
              />
            ) : (
              renderOperandField("right", "Right")
            )}
          </Box>
        )}
        {operands.includes("value") && renderOperandField("value", "Value")}

        {operands.includes("pattern") && (
          <TextField
            label="Pattern"
            size="small"
            fullWidth
            value={(value as { pattern: string }).pattern ?? ""}
            onChange={(e) => patch({ pattern: e.target.value } as never)}
            helperText={errs.find((e) => e.includes("pattern"))}
            error={errs.some((e) => e.includes("pattern"))}
            InputProps={{ style: { fontFamily: "monospace" } }}
          />
        )}
        {operands.includes("flags") && (
          <TextField
            label="Flags (imsu)"
            size="small"
            sx={{ width: 100 }}
            value={(value as { flags?: string }).flags ?? ""}
            onChange={(e) => patch({ flags: e.target.value } as never)}
            helperText={errs.find((e) => e.includes("flag"))}
            error={errs.some((e) => e.includes("flag"))}
          />
        )}

        {operands.includes("conditions") && (
          <Box>
            <Stack spacing={1}>
              {(
                value as { conditions: Userbot.ConditionExpr[] }
              ).conditions.map((sub, i) => (
                <Stack
                  key={i}
                  direction="row"
                  spacing={1}
                  alignItems="flex-start"
                >
                  <Box
                    flexGrow={1}
                    pl={2}
                    sx={{ borderLeft: 1, borderColor: "divider" }}
                  >
                    <ConditionExprEditor
                      value={sub}
                      onChange={(v) => {
                        const conditions = [
                          ...(value as { conditions: Userbot.ConditionExpr[] })
                            .conditions,
                        ]
                        if (v) conditions[i] = v
                        else conditions.splice(i, 1)
                        patch({ conditions } as never)
                      }}
                      headHints={headHints}
                      limits={limits}
                    />
                  </Box>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                      const conditions = (
                        value as { conditions: Userbot.ConditionExpr[] }
                      ).conditions.filter((_, j) => j !== i)
                      patch({ conditions } as never)
                    }}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              <Button
                size="small"
                startIcon={<Add />}
                disabled={depthLimitReached}
                onClick={() =>
                  patch({
                    conditions: [
                      ...(value as { conditions: Userbot.ConditionExpr[] })
                        .conditions,
                      defaultCondition(),
                    ],
                  } as never)
                }
              >
                {depthLimitReached
                  ? `Достигнут предел глубины (${limits?.maxConditionDepth ?? "∞"})`
                  : "Добавить условие"}
              </Button>
            </Stack>
          </Box>
        )}

        {operands.includes("condition") && (
          <Box pl={2} sx={{ borderLeft: 1, borderColor: "divider" }}>
            <ConditionExprEditor
              value={(value as { condition: Userbot.ConditionExpr }).condition}
              onChange={(v) => {
                if (v) patch({ condition: v } as never)
              }}
              headHints={headHints}
              limits={limits}
            />
          </Box>
        )}
      </Stack>

      {errs.length > 0 && (
        <Typography variant="caption" color="error" display="block" mt={1}>
          {errs.join("; ")}
        </Typography>
      )}
      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
    </Box>
  )
}
